import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { Box, Tooltip } from '@mui/material';
import React from 'react';

import {
  OfferCalendarAudienceDto,
  OfferCalendarHiddenReasonDto,
  OfferCalendarVisibilityDto,
  ProcessOfferStatusDto,
} from '@/api/generated';
import { themeColors } from '@/config/theme/themeColors.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';

const reasonKey = (reason: OfferCalendarHiddenReasonDto, status: ProcessOfferStatusDto): string =>
  reason === OfferCalendarHiddenReasonDto.Status && status === ProcessOfferStatusDto.Canceled
    ? 'STATUS_CANCELED'
    : reason;

const icon = (visibility: OfferCalendarVisibilityDto) => {
  if (visibility.visible) {
    return <EventAvailableOutlinedIcon fontSize="small" sx={{ color: themeColors.green }} />;
  }
  if (visibility.audience === OfferCalendarAudienceDto.Nobody) {
    return <EventBusyOutlinedIcon fontSize="small" sx={{ color: themeColors.grey }} />;
  }
  return <VisibilityOffOutlinedIcon fontSize="small" sx={{ color: themeColors.orange }} />;
};

type Props = {
  status: ProcessOfferStatusDto;
  visibility?: OfferCalendarVisibilityDto;
};

export const OfferCalendarVisibilityIndicator: React.FC<Props> = ({ status, visibility }) => {
  const { t } = useTranslationWithPrefix('views.process.offer.details.calendarVisibility');

  if (!visibility) {
    return null;
  }

  const lines = [t(`audience.${visibility.audience}`)];
  if (visibility.hiddenReason) {
    lines.push(`${t('reasonLabel')}: ${t(`reason.${reasonKey(visibility.hiddenReason, status)}`)}`);
  }
  lines.push(visibility.visible ? t('viewer.visible') : t('viewer.hidden'));

  return (
    <Tooltip title={<Box sx={{ whiteSpace: 'pre-line' }}>{lines.join('\n')}</Box>}>
      <Box component="span" sx={{ display: 'inline-flex' }}>
        {icon(visibility)}
      </Box>
    </Tooltip>
  );
};
